import { getTheme, IButtonStyles, mergeStyleSets } from '@fluentui/react';
import { ITextField } from '@fluentui/react/lib/TextField';
import AppContext from 'App/AppContext';
import { IPIconButton, IPLink } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { globalMaxUploadSize } from 'globalConstants';
import {
  clearCopilotLibraryDocumentDragPayload,
  CopilotDocumentDropValidationMessageKey,
  CopilotLibraryDocumentDragPayload,
  getCopilotLibraryDocumentDragPayload,
  getCopilotUploadDocumentFiles,
  validateCopilotDocumentDragOver,
  validateCopilotDocumentDrop,
} from 'models/copilot/copilotLibraryDocumentDragDrop';
import ResourceList from 'models/resourceList';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bytesToSize } from 'utils/string';
import { CopilotChatInputActions, ICopilotChatInputAction } from './CopilotChatInputActions';

interface ICopilotChatInputProps {
  chatDisabled: boolean;
  isWaitingForProcess: boolean;
  isBusy: boolean;
  attachmentMode: boolean;
  canSubmit: boolean;
  hasAttachments: boolean;
  onNewQuestion: (question: string) => void;
  setShowAddDocumentModal?: (show: boolean) => void;
  isLoading: boolean;
  onStop?: () => void;
  canCancel: boolean;
  showWorkingPlaceholder?: boolean;
  // What a disabled input says instead of "Working..." while the running process waits for a card answer
  placeholder?: string;
  targetFolder: ResourceList | undefined;
  setShowFolderPicker?: (show: boolean) => void;
  setShowFileUploader?: (show: boolean) => void;
  onUploadDocument?: () => void;
  onSelectLibraryDocument?: () => void;
  onDropLibraryDocument?: (payload: CopilotLibraryDocumentDragPayload) => void | Promise<void>;
  onDropUploadDocument?: (file: File) => void | Promise<void>;
  documentIngestDisabled?: boolean;
  //app-wide actions of the host, after the document actions (the panel's AI credits)
  extraActions?: ICopilotChatInputAction[];
}

const BORDER_RADIUS = 6;
const WORKING_BORDER_WIDTH = 1;
const WORKING_ANIMATION_MS = 1800;
const DROP_REJECTED_MESSAGE_TIMEOUT_MS = 3000;

export const CopilotChatInput = (props: ICopilotChatInputProps) => {
  const appContext = useContext(AppContext);
  const theme = getTheme();
  const { t } = useTranslation(['translation', 'copilot']);
  const [question, setQuestion] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [dropMessage, setDropMessage] = useState<string | undefined>(undefined);
  const [isDropAccepted, setIsDropAccepted] = useState<boolean>(false);
  const textFieldRef = useRef<ITextField | null>(null);
  const dropRejectedMessageTimeoutRef = useRef<number | undefined>(undefined);

  const inputBackground = appContext.useDarkMode ? theme.palette.neutralDark : theme.palette.white;
  const isWorking = props.isWaitingForProcess;
  const canTypeQuestion = !props.chatDisabled && !props.attachmentMode;
  const hasQuestion = question.trim().length > 0;
  const canSend = props.canSubmit || (hasQuestion && !props.chatDisabled);
  const sendDisabled = props.isBusy || !canSend;
  const showDocumentIngestMenu = !props.attachmentMode && (!!props.onUploadDocument || !!props.onSelectLibraryDocument);
  const documentIngestActionDisabled =
    props.documentIngestDisabled || props.chatDisabled || props.isBusy || props.isLoading;
  const libraryDocumentDropEnabled =
    showDocumentIngestMenu && !documentIngestActionDisabled && !!props.onDropLibraryDocument;
  const uploadDocumentDropEnabled =
    showDocumentIngestMenu && !documentIngestActionDisabled && !!props.onDropUploadDocument;

  const borderColor = dropMessage
    ? isDropAccepted
      ? theme.palette.themePrimary
      : theme.palette.redDark
    : props.chatDisabled
    ? theme.palette.neutralTertiaryAlt
    : isFocused
    ? theme.palette.themePrimary
    : theme.palette.neutralSecondary;

  const workingAnimationColor = theme.palette.themePrimary;
  const dropBackground = dropMessage
    ? isDropAccepted
      ? appContext.useDarkMode
        ? theme.palette.themeDark
        : theme.palette.themeLighter
      : appContext.useDarkMode
      ? theme.palette.redDark
      : theme.semanticColors.errorBackground
    : inputBackground;

  useEffect(() => {
    const styleId = 'chat-input-working-border-angle-keyframes';
    const styleText = `
      @property --chatInputWorkingBorderAngle {
        syntax: '<angle>';
        inherits: false;
        initial-value: 0deg;
      }

      @keyframes chatInputWorkingBorderAngleSpin {
        from {
          --chatInputWorkingBorderAngle: 0deg;
        }
        to {
          --chatInputWorkingBorderAngle: 360deg;
        }
      }
    `;

    let style = document.getElementById(styleId) as HTMLStyleElement | null;

    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }

    style.textContent = styleText;
  }, []);

  const chatInputStyles = mergeStyleSets({
    wrapper: {
      position: 'relative',
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      alignSelf: 'stretch',
      borderRadius: BORDER_RADIUS,
      boxSizing: 'border-box',
      padding: isWorking ? WORKING_BORDER_WIDTH : 0,
      background: isWorking
        ? `conic-gradient(
            from var(--chatInputWorkingBorderAngle, 0deg),
            transparent 0deg,
            transparent 255deg,
            ${workingAnimationColor} 292deg,
            ${workingAnimationColor} 318deg,
            transparent 352deg,
            transparent 360deg
          )`
        : undefined,
      animation: isWorking ? `chatInputWorkingBorderAngleSpin ${WORKING_ANIMATION_MS}ms linear infinite` : undefined,

      selectors: {
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      },
    },
    root: {
      alignSelf: 'stretch',
      background: dropBackground,
      border: `1px solid ${borderColor}`,
      borderRadius: isWorking ? BORDER_RADIUS - WORKING_BORDER_WIDTH : BORDER_RADIUS,
      boxSizing: 'border-box',
      overflow: 'hidden',
      position: 'relative',
      height: 130,
      minWidth: 0,
      width: '100%',
      paddingTop: 8,
      paddingLeft: 8,
      paddingBottom: 4,
      paddingRight: 8,
    },
    dropMessage: {
      alignItems: 'center',
      background: dropBackground,
      color: isDropAccepted ? theme.palette.themePrimary : theme.palette.redDark,
      display: dropMessage ? 'flex' : 'none',
      fontWeight: 600,
      inset: 0,
      justifyContent: 'center',
      padding: 16,
      pointerEvents: dropMessage && !isDropAccepted ? 'auto' : 'none',
      position: 'absolute',
      textAlign: 'center',
      zIndex: 1,
    },
  });

  const clearDropRejectedMessageTimeout = () => {
    if (dropRejectedMessageTimeoutRef.current === undefined) return;
    window.clearTimeout(dropRejectedMessageTimeoutRef.current);
    dropRejectedMessageTimeoutRef.current = undefined;
  };

  const clearDropState = () => {
    clearDropRejectedMessageTimeout();
    setDropMessage(undefined);
    setIsDropAccepted(false);
  };

  const showDropState = (messageKey: CopilotDocumentDropValidationMessageKey | undefined, accepted: boolean) => {
    clearDropRejectedMessageTimeout();
    setIsDropAccepted(accepted);
    setDropMessage(getDropMessageText(messageKey));
  };

  const showRejectedDropState = (messageKey: CopilotDocumentDropValidationMessageKey | undefined) => {
    showDropState(messageKey, false);
    dropRejectedMessageTimeoutRef.current = window.setTimeout(() => {
      setDropMessage(undefined);
      setIsDropAccepted(false);
      dropRejectedMessageTimeoutRef.current = undefined;
    }, DROP_REJECTED_MESSAGE_TIMEOUT_MS);
  };

  useEffect(() => {
    return () => clearDropRejectedMessageTimeout();
  }, []);

  useEffect(() => {
    if (appContext.isMobileView || !canTypeQuestion || props.isLoading || props.isWaitingForProcess) {
      setIsFocused(false);

      return;
    }

    textFieldRef.current?.focus();
  }, [appContext.isMobileView, canTypeQuestion, props.isLoading, props.isWaitingForProcess]);

  const onKeyPress = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    if ((e.nativeEvent as KeyboardEvent).isComposing) return;
    if (e.shiftKey) return;

    sendQuestion();
    e.preventDefault();
  };

  const sendQuestion = () => {
    if (sendDisabled || !props.onNewQuestion) return;
    props.onNewQuestion(question);
    setQuestion('');
  };

  const getDropMessageText = (messageKey: CopilotDocumentDropValidationMessageKey | undefined): string | undefined => {
    if (!messageKey) return undefined;

    switch (messageKey) {
      case 'DropAccept':
        return t('copilot:Chat.Document.DropAccept');
      case 'DropUnavailable':
        return t('copilot:Chat.Document.DropUnavailable');
      case 'DropInvalid':
        return t('copilot:Chat.Document.DropInvalid');
      case 'DropUploadAccept':
        return t('copilot:Chat.Document.DropUploadAccept');
      case 'DropUploadMultiple':
        return t('copilot:Chat.Document.DropUploadMultiple');
      case 'DropUploadTooLarge':
        return t('copilot:Chat.Document.DropUploadTooLarge', { max: bytesToSize(globalMaxUploadSize) });
      case 'DropUploadUnsupported':
        return t('copilot:Chat.Document.DropUploadUnsupported');
    }
  };

  const updateDropState = (dataTransfer: DataTransfer) => {
    const dropState = validateCopilotDocumentDragOver(
      dataTransfer,
      libraryDocumentDropEnabled,
      uploadDocumentDropEnabled,
    );
    showDropState(dropState.messageKey, dropState.accepted);

    return dropState;
  };

  const onDragOverLibraryDocument = (event: React.DragEvent<HTMLDivElement>) => {
    const dropState = updateDropState(event.dataTransfer);
    if (!dropState.handled) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = dropState.accepted ? 'copy' : 'none';
  };

  const onDropLibraryDocument = (event: React.DragEvent<HTMLDivElement>) => {
    const dropState = validateCopilotDocumentDrop(
      event.dataTransfer,
      libraryDocumentDropEnabled,
      uploadDocumentDropEnabled,
    );
    if (!dropState.handled) return;
    event.preventDefault();
    event.stopPropagation();
    try {
      if (!dropState.accepted) {
        showRejectedDropState(dropState.messageKey);

        return;
      }

      clearDropState();
      if (Array.from(event.dataTransfer.types).includes('Files')) {
        const files = getCopilotUploadDocumentFiles(event.dataTransfer);
        void props.onDropUploadDocument?.(files[0]);

        return;
      }

      const payload = getCopilotLibraryDocumentDragPayload(event.dataTransfer);
      if (!payload) return;

      void props.onDropLibraryDocument?.(payload);
    } finally {
      clearCopilotLibraryDocumentDragPayload();
    }
  };

  const attachmentButtonStyles: IButtonStyles = {
    root: {
      width: 30,
      height: 30,
      minWidth: 30,
      minHeight: 30,
      padding: 0,
      background: inputBackground,
      textDecoration: 'none',
    },
    rootHovered: {
      background: inputBackground,
      textDecoration: 'none',
    },
    icon: {
      fontSize: 20,
      lineHeight: 20,
      height: 20,
      width: 20,
      margin: 0,
    },
  };

  const attachmentLink = (text: string, onClick: () => void | undefined, visible: boolean): JSX.Element | null => {
    if (!visible || !onClick) return null;

    return (
      <IPStack horizontal styles={{ root: { alignItems: 'center', gap: 4 } }}>
        <IPStack.Item>
          <IPIconButton
            iconProps={{ iconName: 'Attach' }}
            styles={attachmentButtonStyles}
            onClick={onClick}
            disabled={props.isBusy || props.isLoading}
          />
        </IPStack.Item>
        <IPStack.Item>
          <IPLink
            onClick={onClick}
            styles={{
              root: {
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                textAlign: 'left',
                fontSize: 15,
              },
            }}
            disabled={props.isBusy || props.isLoading}
          >
            {text}
          </IPLink>
        </IPStack.Item>
      </IPStack>
    );
  };

  const documentIngestActions: ICopilotChatInputAction[] = showDocumentIngestMenu
    ? [
        {
          key: 'documentIngest',
          iconName: 'Add',
          size: 'small',
          title: t('translation:General.Button.Add'),
          disabled: documentIngestActionDisabled,
          menuProps: {
            items: [
              {
                key: 'uploadDocument',
                text: t('copilot:Chat.Document.IngestUpload'),
                iconProps: { iconName: 'Upload' },
                disabled: !props.onUploadDocument,
                onClick: () => props.onUploadDocument?.(),
              },
              {
                key: 'libraryDocument',
                text: t('copilot:Chat.Document.IngestLibrary'),
                iconProps: { iconName: 'FabricFolder' },
                disabled: !props.onSelectLibraryDocument,
                onClick: () => props.onSelectLibraryDocument?.(),
              },
            ],
          },
        },
      ]
    : [];
  const chatInputActions: ICopilotChatInputAction[] = [...documentIngestActions, ...(props.extraActions ?? [])];

  const primaryChatInputAction: ICopilotChatInputAction =
    props.canCancel && props.onStop
      ? {
          key: 'stop',
          iconName: 'StopSolid',
          iconColor: theme.palette.redDark,
          title: t('translation:General.Button.Cancel'),
          disabled: false,
          onClick: props.onStop,
        }
      : {
          key: 'send',
          iconName: 'ReturnKey',
          title: t('translation:General.Button.Send'),
          disabled: sendDisabled,
          onClick: sendQuestion,
        };

  return (
    <div
      className={chatInputStyles.wrapper}
      onDragEnter={onDragOverLibraryDocument}
      onDragOver={onDragOverLibraryDocument}
      onDragLeave={clearDropState}
      onDrop={onDropLibraryDocument}
    >
      <IPStack className={chatInputStyles.root} verticalFill>
        <div className={chatInputStyles.dropMessage} onClick={clearDropState}>
          {dropMessage}
        </div>
        <IPStack.Item grow styles={{ root: { minHeight: 0, minWidth: 0, overflow: 'hidden', width: '100%' } }}>
          <IPStack styles={{ root: { width: '100%' } }}>
            {props.attachmentMode && !props.chatDisabled && (
              <IPStack.Item
                styles={{
                  root: {
                    minHeight: 30,
                    height: 'auto',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                  },
                }}
              >
                <IPStack>
                  {attachmentLink(
                    t('copilot:Chat.SelectFolder'),
                    () => props.setShowFolderPicker?.(true),
                    props.targetFolder === undefined,
                  )}
                  {attachmentLink(
                    t('copilot:Chat.AddSharePoint'),
                    () => props.setShowAddDocumentModal?.(true),
                    props.targetFolder !== undefined,
                  )}
                  {attachmentLink(
                    t('copilot:Chat.AddFiles'),
                    () => props.setShowFileUploader?.(true),
                    props.targetFolder !== undefined,
                  )}
                </IPStack>
              </IPStack.Item>
            )}

            {!props.attachmentMode && (
              <IPStack.Item>
                <IPTextField
                  autoFocus={!appContext.isMobileView}
                  componentRef={textFieldRef}
                  hideChrome
                  multiline
                  value={question}
                  onChange={(ev, newValue) => setQuestion(newValue ?? '')}
                  onBlur={() => setIsFocused(false)}
                  onFocus={() => setIsFocused(true)}
                  onKeyDown={onKeyPress}
                  placeholder={
                    props.isLoading
                      ? t('copilot:Chat.InputPlaceholderLoading')
                      : props.chatDisabled && !props.isWaitingForProcess && props.placeholder
                      ? props.placeholder
                      : props.chatDisabled && (props.isWaitingForProcess || props.showWorkingPlaceholder)
                      ? t('copilot:Chat.InputPlaceholderWorking')
                      : props.chatDisabled
                      ? t('copilot:Chat.InputPlaceholderNoProcess')
                      : t('copilot:Chat.InputPlaceholder')
                  }
                  maxLength={512}
                  disabled={!canTypeQuestion}
                  styles={{
                    root: {
                      width: '100%',
                    },
                    fieldGroup: {
                      height: 'auto',
                    },
                    field: {
                      resize: 'none',
                      maxHeight: 80,
                    },
                  }}
                />
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
        <IPStack.Item styles={{ root: { flexShrink: 0 } }}>
          <CopilotChatInputActions
            actions={chatInputActions}
            backgroundColor={inputBackground}
            primaryAction={primaryChatInputAction}
          />
        </IPStack.Item>
      </IPStack>
    </div>
  );
};
